import React, { useState, useEffect, useContext } from 'react'
import GenericModal from '../../../components/modal/GenericModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SpinnerLoading from '../../../components/pageComponents/SpinnerLoading';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { getConvocatoriaAPI, listarArchivosApoyoAPI } from '../../../consumers/backendApisUrls';
import { downloadFile } from '../../../consumers/backendConsumer';
import { AuthContext } from '../../../hooks/context/authContext';

// ──────────────────────────────────────────────────────────────────────────
// Modal "Lineamientos" (30/09/2026, a pedido de Jody). Se abre desde el botón
// "Lineamientos" de RevisionInicialGDH.jsx (handleVerLineamientos) — antes
// ese botón solo hacía console.log('ver lineamientos y adjuntos').
//
// Muestra TP_Convocatoria.Lineamientos y los archivos de apoyo de la
// convocatoria (máx. 2), reusando los mismos endpoints que ya usa el resto
// del módulo: GetConvocatoria/{id} (getConvocatoriaAPI, trae "Lineamientos" —
// mismo campo que ya consume DetalleConvocatoriaModal.jsx) y
// ListarArchivosApoyo/{id} (listarArchivosApoyoAPI, mismo shape que
// NuevaConvocatoriaMasivo.jsx: IdArchivo/Path/OriginalFileName).
//
// Lineamientos se guarda como HTML (tags <p>/<strong>/etc., ver
// InputEditorHtml en NuevaConvocatoriaMasivo.jsx), así que se muestra en una
// caja de solo lectura con scroll que renderiza ese HTML (mismo patrón/estilo
// ya usado en DetalleConvocatoriaModal.jsx y PostularConvocatoriaModal.jsx)
// en vez de un <textarea> real — un textarea mostraría los tags literales en
// vez del texto con formato.
//
// Los archivos de apoyo se listan igual que en NuevaConvocatoriaMasivo.jsx
// (ícono de clip + nombre subrayado, clic para descargar vía
// consumers/backendConsumer.downloadFile con el token de AuthContext), pero
// sin los íconos de eliminar/subir — acá la ficha es de solo lectura.
// ──────────────────────────────────────────────────────────────────────────

const LineamientosTrasladoPromocion = ({ toggleModal, idConvocatoria }) => {
    const { state } = useContext(AuthContext);
    const [executeGet] = useGetRequest();

    const [isLoading, setIsLoading] = useState(true);
    const [lineamientos, setLineamientos] = useState(null);
    const [archivosApoyo, setArchivosApoyo] = useState([]);

    useEffect(() => {
        if (!idConvocatoria) {
            setIsLoading(false);
            return;
        }
        let cancelado = false;

        (async () => {
            const [convocatoriaResp, archivosResp] = await Promise.all([
                executeGet(getConvocatoriaAPI(idConvocatoria)),
                executeGet(listarArchivosApoyoAPI(idConvocatoria)),
            ]);
            if (cancelado) return;
            setLineamientos(convocatoriaResp?.data?.Lineamientos ?? null);
            setArchivosApoyo(archivosResp?.data ?? []);
            setIsLoading(false);
        })();

        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idConvocatoria]);

    const handleDescargarArchivo = (archivo) => {
        downloadFile(state.token, archivo.Path, archivo.OriginalFileName);
    };

    if (isLoading) {
        return (
            <GenericModal showModal={toggleModal} titulo="Lineamientos" width="700px">
                <div className="modal-body text-center py-5"><SpinnerLoading /></div>
            </GenericModal>
        );
    }

    return (
        <GenericModal showModal={toggleModal} titulo="Lineamientos" width="700px">
            <div className="modal-body pt-3">
                <label className="modal-label d-flex">Lineamientos</label>
                {lineamientos
                    ? <div
                        style={{ maxHeight: "16rem", overflowY: "auto", padding: "8px 12px", border: "1px solid #ced4da", borderRadius: "4px" }}
                        dangerouslySetInnerHTML={{ __html: lineamientos }} />
                    : <div className="text-muted"
                        style={{ padding: "8px 12px", border: "1px solid #ced4da", borderRadius: "4px" }}>
                        Sin lineamientos registrados.
                    </div>
                }

                <div className="mt-3">
                    <label className="modal-label d-flex">Archivos de apoyo</label>
                    {archivosApoyo.length > 0
                        ? archivosApoyo.map((archivo, index) => (
                            <div key={archivo.IdArchivo ?? archivo.Path ?? index} className="mb-1">
                                <span className="text-truncate d-inline-block" style={{ maxWidth: "100%" }}
                                    role="button" title={archivo.OriginalFileName}
                                    onClick={() => handleDescargarArchivo(archivo)}>
                                    <FontAwesomeIcon icon="paperclip" className="mr-2" />
                                    <u>{archivo.OriginalFileName}</u>
                                </span>
                            </div>
                        ))
                        : <div className="text-muted">Sin archivos de apoyo.</div>
                    }
                </div>
            </div>
        </GenericModal>
    );
};

export default LineamientosTrasladoPromocion;